import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, Phone, Clock } from "lucide-react";
import { format } from "date-fns";
import type { VoiceAgentSession } from "@/hooks/useVoiceAgentAnalytics";

interface VoiceAgentSessionsTableProps {
  sessions: VoiceAgentSession[];
  isLoading: boolean;
  onSelectSession: (sessionId: string) => void;
  selectedSessionId: string | null;
}

const VoiceAgentSessionsTable = ({ 
  sessions, 
  isLoading, 
  onSelectSession,
  selectedSessionId 
}: VoiceAgentSessionsTableProps) => {
  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      active: "default",
      completed: "secondary",
      error: "destructive",
      disconnected: "outline",
    };
    
    const colors: Record<string, string> = {
      active: "bg-green-500/20 text-green-700 border-green-500/30",
      completed: "bg-blue-500/20 text-blue-700 border-blue-500/30",
      error: "bg-red-500/20 text-red-700 border-red-500/30",
      disconnected: "bg-gray-500/20 text-gray-700 border-gray-500/30",
    };

    return (
      <Badge variant={variants[status] || "outline"} className={colors[status] || ""}>
        {status}
      </Badge>
    );
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "—";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12">
        <Phone className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground">No sessions yet</h3>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Voice agent conversation sessions will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Session ID</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow 
              key={session.id}
              className={selectedSessionId === session.id ? "bg-muted/50" : ""}
            >
              <TableCell className="font-mono text-sm">
                {session.session_id.substring(0, 8)}...
              </TableCell>
              <TableCell>
                {format(new Date(session.started_at), "MMM d, yyyy HH:mm")}
              </TableCell>
              <TableCell className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                {formatDuration(session.duration_seconds)}
              </TableCell>
              <TableCell>{getStatusBadge(session.status)}</TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSelectSession(session.id)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default VoiceAgentSessionsTable;
